import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';

import { QuestionType } from '@/constants/QuestionType';

// Validation translation keys
const atLeastOneOptionRequired: TranslationKey = 'answer.validation.atLeastOneOptionRequired';
const minThreeCharacters: TranslationKey = 'answer.validation.minThreeCharacters';
const ratingRequired: TranslationKey = 'answer.validation.ratingRequired';
const allOptionsMustBeRanked: TranslationKey = 'answer.validation.allOptionsMustBeRanked';
const otherOptionRequired: TranslationKey = 'answer.validation.otherOptionRequired';

/**
 * Check if the "Other" option is selected in a localization-safe way
 * by comparing selected values against the actual question options
 */
export const isOtherOptionSelected = (
    selectedValues: string[],
    question: ReviewQuestion,
): boolean => {
    if (!question.reviewQuestionOptions) return false;

    return selectedValues.some((selectedValue) => {
        // selectedValues now contains option IDs, so we can directly check the option
        const option = question.reviewQuestionOptions?.find((opt) => opt.id === selectedValue);
        return option && option.isSystemGenerated;
    });
};

/**
 * Form values for a single question answer
 */
export interface QuestionAnswerFormValues {
    questionId: string;
    questionType: string;
    selectedValues: string[];
    intValue?: number;
    boolValue?: boolean;
    textValue?: string;
    comment?: string;
}

/**
 * Creates a dynamic Zod schema for validating a single question answer
 * based on the question type and requirements
 */
export const createQuestionAnswerSchema = (question: ReviewQuestion) => {
    const baseSchema = z.object({
        questionId: z.string(),
        questionType: z.string(),
        selectedValues: z.array(z.string()),
        intValue: z.number().optional(),
        boolValue: z.boolean().optional(),
        textValue: z.string().optional(),
        comment: z.string().optional(),
    });

    // Section title questions don't require validation
    if (question.questionType === QuestionType.SECTION_TITLE) {
        return baseSchema;
    }

    switch (question.questionType) {
        case QuestionType.CHECKBOX:
        case QuestionType.EMOJI_CHOICE:
        case QuestionType.MULTI_CHOICE:
            return baseSchema
                .refine((data) => data.selectedValues.length >= 1, {
                    message: atLeastOneOptionRequired,
                    path: ['selectedValues'],
                })
                .refine(
                    (data) => {
                        // If "Other" option is selected and question has other option enabled, textValue is required
                        if (
                            question.hasOtherOption &&
                            isOtherOptionSelected(data.selectedValues, question)
                        ) {
                            return data.textValue !== undefined && data.textValue.trim().length > 0;
                        }
                        return true;
                    },
                    {
                        message: otherOptionRequired,
                        path: ['textValue'],
                    },
                );

        case QuestionType.YES_NO:
            return baseSchema.refine((data) => data.boolValue !== undefined, {
                message: atLeastOneOptionRequired,
                path: ['boolValue'],
            });

        case QuestionType.PLAIN_TEXT:
            return baseSchema.refine(
                (data) => data.textValue !== undefined && data.textValue.trim().length >= 3,
                {
                    message: minThreeCharacters,
                    path: ['textValue'],
                },
            );

        case QuestionType.RATING_SCALE:
            return baseSchema.refine((data) => data.intValue !== undefined, {
                message: ratingRequired,
                path: ['intValue'],
            });

        case QuestionType.RANKING:
            return baseSchema.refine(
                (data) => {
                    const optionsCount = question.reviewQuestionOptions?.length ?? 0;
                    return data.selectedValues.length === optionsCount;
                },
                {
                    message: allOptionsMustBeRanked,
                    path: ['selectedValues'],
                },
            );

        default:
            return baseSchema;
    }
};

/**
 * Helper to get default form values for a question
 */
export const getDefaultAnswerValues = (question: ReviewQuestion): QuestionAnswerFormValues => {
    return {
        questionId: question.id ?? '',
        questionType: question.questionType,
        selectedValues: [],
        intValue: undefined,
        boolValue: undefined,
        textValue: undefined,
        comment: undefined,
    };
};
